import { Typography, Progress, Button } from "antd";
import { TbArrowUp, TbArrowDown, TbCheck, TbAlertCircle, TbChevronRight } from "react-icons/tb";
import { formatMoney, round } from "utils/functions";

const { Text } = Typography;

function formatMoneyCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "")}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(2).replace(/\.?0+$/, "")}k`;
  return formatMoney(value);
}

export function ComparisonPopoverContent({
  paid,
  expected,
  payback,
  extrapolated,
  onViewAnalysis,
}: Readonly<{
  paid: number;
  expected?: number;
  payback?: number;
  extrapolated: boolean;
  onViewAnalysis?: () => unknown;
}>) {
  const hasExpected = expected !== undefined;
  const hasPayback = payback !== undefined;
  const delta = hasExpected ? paid - expected : 0;
  const isOver = hasExpected && delta > 0;
  const isUnder = hasExpected && delta < 0;
  const isExact = hasExpected && delta === 0;
  const progress = hasExpected ? Math.min((paid / expected) * 100, 100) : 0;

  const statusColor = !hasExpected ? "#a1a1aa" : isOver ? "#168F5A" : isUnder ? "#C73E45" : "#7c3aed";

  const statusIcon = !hasExpected ? (
    <TbAlertCircle size={12} className="stroke-[3]" color={statusColor} />
  ) : isOver ? (
    <TbArrowUp size={12} className="stroke-[3]" color={statusColor} />
  ) : isUnder ? (
    <TbArrowDown size={12} className="stroke-[3]" color={statusColor} />
  ) : (
    <TbCheck size={12} className="stroke-[3]" color={statusColor} />
  );

  const statusText = !hasExpected
    ? "No data available"
    : isExact
      ? "On track"
      : isOver
        ? `Overpaid by ${(extrapolated ? "almost " : "") + formatMoneyCompact(Math.abs(delta))}`
        : `Underpaid by ${(extrapolated ? "atleast " : "") + formatMoneyCompact(Math.abs(delta))}`;

  return (
    <div className="w-full rounded-lg p-2.5 space-y-2">
      <div className="flex items-stretch gap-2">
        <div className="flex-1 rounded-md bg-[#F5F3FF] border border-[#E9E5FB] px-2 py-1.5">
          <Text className="block text-[10px] text-zinc-400 leading-tight font-medium">Paid</Text>
          <Text strong className="text-sm leading-tight block">
            {formatMoneyCompact(paid)}
          </Text>
        </div>

        <div className="flex-1 rounded-md bg-zinc-50 border border-zinc-100 px-2 py-1.5">
          <Text className="block text-[10px] text-zinc-400 leading-tight font-medium">Expected</Text>
          {hasExpected ? (
            <Text strong className="text-sm leading-tight block">
              {formatMoneyCompact(expected)}
              {hasPayback ? (
                <span className="text-xs font-normal opacity-60 ml-1">({round((expected / payback) * 100, 2)}%)</span>
              ) : null}
            </Text>
          ) : (
            <Text className="text-sm leading-tight block text-zinc-400">—</Text>
          )}
        </div>
      </div>

      {hasExpected && (
        <Progress
          percent={progress}
          showInfo={false}
          size="small"
          strokeColor={statusColor}
          trailColor="#F4F4F5"
          className="!mb-0"
        />
      )}

      <div className="flex items-center justify-start gap-1">
        {statusIcon}
        <Text className="text-[11px] font-semibold leading-none" style={{ color: statusColor }}>
          {statusText}
        </Text>
        <Button
          type="text"
          size="small"
          hidden={!onViewAnalysis}
          onClick={onViewAnalysis}
          className="text-[11px] font-semibold gap-1 self-end text-indigo-500 ml-auto"
        >
          Analysis
          <TbChevronRight className="inline" />
        </Button>
      </div>
    </div>
  );
}
